/**
 * Approval Package Wizard — 4-step flow.
 *
 *   1. Materials   — pick artifacts of any registered type (policies, work
 *                    plans, etc.). Multi-type selection in one bundle.
 *   2. Recipients  — internal user typeahead + external email chips
 *   3. Settings    — deadline, quorum rule, invite message
 *   4. Review      — preview, send (or save as draft)
 *
 * Accepts an optional `seededArtifact` { artifact_type, artifact_id } so that
 * deep-link buttons from COA, PPM, etc. can drop the user straight into step
 * 1 with the relevant artifact pre-selected.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL;

function defaultDeadlineISO() {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  d.setHours(17, 0, 0, 0);
  return d.toISOString().slice(0, 16);
}

function suggestPackageName(items) {
  if (items.length === 0) return '';
  if (items.length === 1) return `${items[0].title}`;
  // Mixed bundle — prefer the artifact_type label of the dominant type
  const byType = {};
  for (const it of items) byType[it.artifact_type] = (byType[it.artifact_type] || 0) + 1;
  const dom = Object.entries(byType).sort((a, b) => b[1] - a[1])[0][0];
  if (dom === 'coa_work_plan') return `${items[0].title} + ${items.length - 1} related items`;
  return `${items.length} items for approval`;
}

export default function PackageWizard({ onCancel, onCreated, seededArtifact = null }) {
  const [step, setStep] = useState(1);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  // Step 1 — type-tabbed picker
  const [artifactTypes, setArtifactTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(seededArtifact?.artifact_type || 'ppm_policy_version');
  const [artifacts, setArtifacts] = useState([]);
  const [loadingArtifacts, setLoadingArtifacts] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedItems, setSelectedItems] = useState([]); // [{artifact_type, artifact_id, title, subtitle, version_label}]
  const [suggestions, setSuggestions] = useState([]); // related items the wizard offers as "add this too?"
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Step 2
  const [recipients, setRecipients] = useState([]);
  const [internalQuery, setInternalQuery] = useState('');
  const [internalResults, setInternalResults] = useState([]);
  const [externalEmail, setExternalEmail] = useState('');
  const [externalName, setExternalName] = useState('');
  const [externalRole, setExternalRole] = useState('board');

  // Step 3
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [message, setMessage] = useState('');
  const [deadline, setDeadline] = useState(defaultDeadlineISO());
  const [quorumRule, setQuorumRule] = useState('all_required');
  const [quorumThreshold, setQuorumThreshold] = useState(2);

  // --- Load artifact-type registry ------------------------------------------
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/approvals/artifact-types`, { credentials: 'include' });
        const d = await r.json();
        if (d.success && d.artifact_types?.length > 0) setArtifactTypes(d.artifact_types);
      } catch {}
    })();
  }, []);

  // --- Load artifacts for the active type ----------------------------------
  const loadArtifacts = useCallback(async () => {
    setLoadingArtifacts(true);
    try {
      const qs = new URLSearchParams({ type: selectedType });
      if (search) qs.set('q', search);
      const r = await fetch(`${API}/api/approvals/artifacts?${qs.toString()}`, { credentials: 'include' });
      const d = await r.json();
      if (d.success) setArtifacts(d.artifacts || []);
    } catch {}
    setLoadingArtifacts(false);
  }, [selectedType, search]);

  useEffect(() => { loadArtifacts(); }, [loadArtifacts]);

  // --- Load dependency suggestions when the bundle has at least one item ----
  useEffect(() => {
    if (selectedItems.length === 0) { setSuggestions([]); return; }
    const anchor = selectedItems[0]; // the first picked item is the anchor
    let cancel = false;
    setLoadingSuggestions(true);
    (async () => {
      try {
        const r = await fetch(
          `${API}/api/approvals/dependencies?type=${encodeURIComponent(anchor.artifact_type)}&id=${encodeURIComponent(anchor.artifact_id)}`,
          { credentials: 'include' }
        );
        const d = await r.json();
        if (cancel) return;
        if (d.success) {
          // Filter out anything already in the bundle.
          const inBundle = new Set(selectedItems.map((x) => `${x.artifact_type}:${x.artifact_id}`));
          setSuggestions((d.suggestions || []).filter((s) => !inBundle.has(`${s.artifact_type}:${s.artifact_id}`)));
        }
      } catch {}
      if (!cancel) setLoadingSuggestions(false);
    })();
    return () => { cancel = true; };
  }, [selectedItems]);

  // --- Pre-seed from deep link ---------------------------------------------
  useEffect(() => {
    if (!seededArtifact) return;
    const { artifact_type, artifact_id } = seededArtifact;
    if (!artifact_type || !artifact_id) return;
    (async () => {
      try {
        const r = await fetch(
          `${API}/api/approvals/artifacts?type=${encodeURIComponent(artifact_type)}`,
          { credentials: 'include' }
        );
        const d = await r.json();
        if (!d.success) return;
        const match = (d.artifacts || []).find((a) => a.artifact_id === artifact_id);
        if (match) {
          setSelectedItems((prev) => {
            if (prev.find((x) => x.artifact_id === match.artifact_id && x.artifact_type === match.artifact_type)) return prev;
            return [...prev, {
              artifact_type: match.artifact_type,
              artifact_id: match.artifact_id,
              title: match.title,
              subtitle: match.subtitle,
              version_label: match.version_label,
            }];
          });
          if (!name) setName(suggestPackageName([{ ...match, artifact_type: match.artifact_type }]));
        }
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seededArtifact?.artifact_type, seededArtifact?.artifact_id]);

  // --- Live internal-user typeahead -----------------------------------------
  useEffect(() => {
    if (!internalQuery || internalQuery.length < 2) { setInternalResults([]); return; }
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/approvals/users?q=${encodeURIComponent(internalQuery)}`,
          { credentials: 'include' });
        const data = await res.json();
        if (!cancel && data.success) setInternalResults(data.users || []);
      } catch {}
    }, 200);
    return () => { cancel = true; clearTimeout(t); };
  }, [internalQuery]);

  const toggleItem = (a) => {
    if (!a.selectable) return;
    setSelectedItems((prev) => {
      const key = (x) => `${x.artifact_type}:${x.artifact_id}`;
      const exists = prev.find((x) => key(x) === key(a));
      if (exists) return prev.filter((x) => key(x) !== key(a));
      const next = [...prev, {
        artifact_type: a.artifact_type,
        artifact_id: a.artifact_id,
        title: a.title,
        subtitle: a.subtitle,
        version_label: a.version_label,
      }];
      if (!name) setName(suggestPackageName(next));
      return next;
    });
  };

  const addInternalRecipient = (u, role = 'committee') => {
    setRecipients((prev) => {
      if (prev.find((r) => r.email.toLowerCase() === (u.email || '').toLowerCase())) return prev;
      return [...prev, {
        user_id: u.id,
        email: u.email,
        display_name: u.name,
        role,
        required: role !== 'observer',
      }];
    });
    setInternalQuery('');
    setInternalResults([]);
  };

  const addExternalRecipient = () => {
    const email = (externalEmail || '').trim().toLowerCase();
    if (!email.includes('@')) { setError('Please enter a valid email address.'); return; }
    setError('');
    setRecipients((prev) => {
      if (prev.find((r) => r.email.toLowerCase() === email)) return prev;
      return [...prev, {
        email,
        display_name: (externalName || '').trim() || null,
        role: externalRole,
        required: externalRole !== 'observer',
      }];
    });
    setExternalEmail('');
    setExternalName('');
  };

  const removeRecipient = (email) =>
    setRecipients((prev) => prev.filter((r) => r.email.toLowerCase() !== email.toLowerCase()));

  const updateRecipientField = (email, field, value) =>
    setRecipients((prev) =>
      prev.map((r) =>
        r.email.toLowerCase() === email.toLowerCase()
          ? { ...r, [field]: value, ...(field === 'role' ? { required: value !== 'observer' } : {}) }
          : r
      )
    );

  const canAdvance =
    (step === 1 && selectedItems.length > 0) ||
    (step === 2 && recipients.length > 0) ||
    (step === 3 && !!name.trim()) ||
    step === 4;

  const submit = useCallback(async (sendNow) => {
    setSending(true);
    setError('');
    try {
      const body = {
        name: name.trim(),
        description: description || null,
        message: message || null,
        deadline_at: deadline ? new Date(deadline).toISOString() : null,
        quorum_rule: quorumRule,
        quorum_threshold: quorumRule === 'custom' ? Number(quorumThreshold) || 1 : null,
        items: selectedItems.map((it, i) => ({
          artifact_type: it.artifact_type,
          artifact_id: it.artifact_id,
          display_order: i,
        })),
        recipients: recipients.map((r) => ({
          user_id: r.user_id || null,
          email: r.email,
          display_name: r.display_name || null,
          role: r.role,
          required: r.required,
        })),
      };
      const res = await fetch(`${API}/api/approvals/packages`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to create package');

      if (sendNow) {
        const sendRes = await fetch(`${API}/api/approvals/packages/${data.package.id}/send`, {
          method: 'POST', credentials: 'include',
        });
        const sendData = await sendRes.json();
        if (!sendData.success) throw new Error(sendData.message || 'Failed to send invitations');
      }
      onCreated?.(data.package.id);
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  }, [name, description, message, deadline, quorumRule, quorumThreshold, selectedItems, recipients, onCreated]);

  // ===== RENDER =============================================================
  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <button onClick={onCancel} className="text-xs text-slate-500 hover:text-slate-700 mb-4 inline-flex items-center gap-1">
          <span>←</span> Back to SignoffHub
        </button>

        <h2 className="text-xl font-bold text-slate-800 tracking-tight mb-1">Create sign-off package</h2>
        <p className="text-sm text-slate-500 mb-6">Bundle what needs sign-off and invite reviewers in four quick steps.</p>

        {/* Stepper */}
        <ol className="flex items-center justify-between mb-8">
          {['Choose what to approve', 'Reviewers', 'Settings', 'Review & send'].map((label, idx) => {
            const n = idx + 1;
            const active = step === n;
            const done = step > n;
            return (
              <li key={label} className="flex-1 flex items-center">
                <div className={`flex items-center gap-2 ${active ? 'text-indigo-700' : done ? 'text-emerald-600' : 'text-slate-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                    active ? 'bg-indigo-600 text-white border-indigo-600' :
                    done   ? 'bg-emerald-500 text-white border-emerald-500' :
                             'bg-white border-slate-200 text-slate-400'
                  }`}>
                    {done ? '✓' : n}
                  </div>
                  <span className="text-xs font-semibold">{label}</span>
                </div>
                {n < 4 && <div className={`flex-1 h-px mx-3 ${done ? 'bg-emerald-300' : 'bg-slate-200'}`} />}
              </li>
            );
          })}
        </ol>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 min-h-[460px]">
          {step === 1 && (
            <Step1Materials
              artifactTypes={artifactTypes}
              selectedType={selectedType}
              onSelectType={(t) => { setSelectedType(t); setSearch(''); }}
              artifacts={artifacts}
              loading={loadingArtifacts}
              search={search}
              onSearch={setSearch}
              selectedItems={selectedItems}
              onToggleItem={toggleItem}
              onRemoveItem={(it) => setSelectedItems((prev) =>
                prev.filter((x) => !(x.artifact_type === it.artifact_type && x.artifact_id === it.artifact_id))
              )}
              suggestions={suggestions}
              loadingSuggestions={loadingSuggestions}
            />
          )}

          {step === 2 && (
            <Step2Recipients
              recipients={recipients}
              internalQuery={internalQuery}
              setInternalQuery={setInternalQuery}
              internalResults={internalResults}
              addInternal={addInternalRecipient}
              externalEmail={externalEmail}
              setExternalEmail={setExternalEmail}
              externalName={externalName}
              setExternalName={setExternalName}
              externalRole={externalRole}
              setExternalRole={setExternalRole}
              addExternal={addExternalRecipient}
              removeRecipient={removeRecipient}
              updateRecipientField={updateRecipientField}
            />
          )}

          {step === 3 && (
            <Step3Settings
              name={name} setName={setName}
              description={description} setDescription={setDescription}
              message={message} setMessage={setMessage}
              deadline={deadline} setDeadline={setDeadline}
              quorumRule={quorumRule} setQuorumRule={setQuorumRule}
              quorumThreshold={quorumThreshold} setQuorumThreshold={setQuorumThreshold}
            />
          )}

          {step === 4 && (
            <Step4Review
              name={name}
              description={description}
              message={message}
              deadline={deadline}
              quorumRule={quorumRule}
              quorumThreshold={quorumThreshold}
              selectedItems={selectedItems}
              recipients={recipients}
            />
          )}

          {error && (
            <div className="mt-4 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs">{error}</div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-5">
          <button
            onClick={() => (step === 1 ? onCancel() : setStep(step - 1))}
            className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </button>
          {step < 4 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canAdvance}
              className={`px-5 py-2 text-sm font-semibold rounded-lg ${
                canAdvance
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              }`}
            >
              Continue
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => submit(false)}
                disabled={sending || !name.trim()}
                className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Save as draft
              </button>
              <button
                onClick={() => submit(true)}
                disabled={sending || !name.trim() || recipients.length === 0 || selectedItems.length === 0}
                className="px-5 py-2 text-sm font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm disabled:opacity-50"
              >
                {sending ? 'Sending…' : 'Send invitations'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============== Step 1 — Materials =========================================
function Step1Materials({
  artifactTypes, selectedType, onSelectType, artifacts, loading, search, onSearch,
  selectedItems, onToggleItem, onRemoveItem,
  suggestions = [], loadingSuggestions = false,
}) {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-1">Choose what to ratify</h3>
      <p className="text-xs text-slate-500 mb-4">
        Pick one or more items from any category. The exact content your reviewers see is captured immutably when you click Send — no drift, ever.
      </p>

      {/* Type tabs */}
      {artifactTypes.length > 1 && (
        <div className="flex gap-1 mb-3 border-b border-slate-200">
          {artifactTypes.map((t) => (
            <button
              key={t.artifact_type}
              onClick={() => onSelectType(t.artifact_type)}
              className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-all ${
                selectedType === t.artifact_type
                  ? 'border-indigo-600 text-indigo-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      <input
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder="Search…"
        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />

      <div className="max-h-72 overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-100">
        {loading ? (
          <div className="text-center py-8 text-xs text-slate-400">Loading…</div>
        ) : artifacts.length === 0 ? (
          <div className="text-center py-8 text-xs text-slate-400">Nothing here yet.</div>
        ) : (
          artifacts.map((a) => {
            const selected = selectedItems.find((x) => x.artifact_type === a.artifact_type && x.artifact_id === a.artifact_id);
            return (
              <label key={`${a.artifact_type}:${a.artifact_id || 'none'}`}
                     className={`flex items-center px-3 py-2.5 cursor-pointer ${a.selectable ? 'hover:bg-slate-50' : 'opacity-60 cursor-not-allowed'}`}>
                <input
                  type="checkbox"
                  className="rounded text-indigo-600 mr-3"
                  checked={!!selected}
                  disabled={!a.selectable}
                  onChange={() => onToggleItem(a)}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">
                    {a.title}
                    {a.subtitle && <span className="text-slate-400 font-normal"> — {a.subtitle}</span>}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {a.version_label || ''}
                  </div>
                </div>
              </label>
            );
          })
        )}
      </div>

      {/* Selected bundle */}
      {selectedItems.length > 0 && (
        <div className="mt-5">
          <div className="text-xs uppercase font-bold tracking-wider text-slate-500 mb-2">
            In this package ({selectedItems.length})
          </div>
          <ul className="space-y-1">
            {selectedItems.map((it) => (
              <li key={`${it.artifact_type}:${it.artifact_id}`}
                  className="flex items-center justify-between bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm text-slate-800 truncate">
                    {it.title}
                    {it.subtitle && <span className="text-slate-400"> — {it.subtitle}</span>}
                  </div>
                  <div className="text-[11px] text-slate-500">{it.version_label}</div>
                </div>
                <button
                  onClick={() => onRemoveItem(it)}
                  className="text-xs text-slate-500 hover:text-red-600"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Suggested related items (Phase 3) — explicit dependencies, then a
          dept/category heuristic. One-click add. Quiet when there are no
          suggestions or while we're loading the first batch. */}
      {selectedItems.length > 0 && (suggestions.length > 0 || loadingSuggestions) && (
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase font-bold tracking-wider text-amber-700">
              Related items you may want to include
            </div>
            {loadingSuggestions && <span className="text-[10px] text-slate-400">Looking…</span>}
          </div>
          {suggestions.length > 0 ? (
            <ul className="space-y-1">
              {suggestions.map((s) => (
                <li key={`sug-${s.artifact_type}:${s.artifact_id}`}
                    className="flex items-center justify-between bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-800 truncate">{s.title}</span>
                      {s.suggestion_source === 'explicit' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold uppercase tracking-wider">Linked</span>
                      )}
                      {s.suggestion_source === 'inferred' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold uppercase tracking-wider">Suggested</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {s.subtitle ? `${s.subtitle} · ` : ''}{s.version_label || ''}
                    </div>
                  </div>
                  <button
                    onClick={() => onToggleItem({ ...s, selectable: true })}
                    className="text-xs px-2.5 py-1 rounded-md bg-white border border-amber-300 text-amber-800 font-semibold hover:bg-amber-100"
                  >
                    + Add
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
    </div>
  );
}

// =============== Step 2 — Recipients ========================================
function Step2Recipients({
  recipients, internalQuery, setInternalQuery, internalResults, addInternal,
  externalEmail, setExternalEmail, externalName, setExternalName,
  externalRole, setExternalRole, addExternal,
  removeRecipient, updateRecipientField,
}) {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-1">Choose reviewers</h3>
      <p className="text-xs text-slate-500 mb-4">
        Pick anyone on your team or invite outside committee or board members by email — they don't need a Core account.
      </p>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="text-xs uppercase font-bold tracking-wider text-slate-500 mb-1">Internal users</div>
          <input
            value={internalQuery}
            onChange={(e) => setInternalQuery(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {internalResults.length > 0 && (
            <div className="border border-slate-100 rounded-lg mt-2 max-h-48 overflow-y-auto divide-y divide-slate-100">
              {internalResults.map((u) => (
                <button
                  key={u.id}
                  onClick={() => addInternal(u, 'committee')}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50"
                >
                  <div className="text-sm font-medium text-slate-800">{u.name}</div>
                  <div className="text-[11px] text-slate-500">{u.email}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="text-xs uppercase font-bold tracking-wider text-slate-500 mb-1">External (board / committee)</div>
          <input
            value={externalEmail}
            onChange={(e) => setExternalEmail(e.target.value)}
            placeholder="board.member@example.com"
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 mb-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <input
            value={externalName}
            onChange={(e) => setExternalName(e.target.value)}
            placeholder="Display name (optional)"
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 mb-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <div className="flex gap-2">
            <select
              value={externalRole}
              onChange={(e) => setExternalRole(e.target.value)}
              className="text-sm border border-slate-200 rounded-lg px-2 py-2"
            >
              <option value="board">Board</option>
              <option value="committee">Committee</option>
              <option value="observer">Observer</option>
            </select>
            <button
              onClick={addExternal}
              className="flex-1 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold"
            >
              Add reviewer
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="text-xs uppercase font-bold tracking-wider text-slate-500 mb-2">
          Reviewers ({recipients.length})
        </div>
        {recipients.length === 0 && (
          <div className="text-xs text-slate-400 py-3">No reviewers added yet.</div>
        )}
        <div className="space-y-2">
          {recipients.map((r) => (
            <ReviewerChip
              key={r.email}
              recipient={r}
              onRoleChange={(v) => updateRecipientField(r.email, 'role', v)}
              onRequiredChange={(v) => updateRecipientField(r.email, 'required', v)}
              onRemove={() => removeRecipient(r.email)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ReviewerChip — recipient row with inline decision-history strip the moment
// they're added. Compliance officers see "this person responds in 2 days +
// approves 90%" before they ever pick someone, which is the maximum-impact
// moment for that intelligence.
function ReviewerChip({ recipient: r, onRoleChange, onRequiredChange, onRemove }) {
  const [stats, setStats] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(
          `${API}/api/approvals/reviewers/${encodeURIComponent(r.email)}/stats`,
          { credentials: 'include' }
        );
        const d = await res.json();
        if (!cancel && d.success) setStats(d.stats);
      } catch {}
      if (!cancel) setLoaded(true);
    })();
    return () => { cancel = true; };
  }, [r.email]);

  const hasHistory = stats && stats.invitations > 0;

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-800 truncate">{r.display_name || r.email}</div>
          <div className="text-[11px] text-slate-500">
            {r.email}{r.user_id ? ' · internal user' : ' · external invitee'}
          </div>
        </div>
        <select
          value={r.role}
          onChange={(e) => onRoleChange(e.target.value)}
          className="text-xs border border-slate-200 rounded px-2 py-1 bg-white"
        >
          <option value="committee">Committee</option>
          <option value="board">Board</option>
          <option value="observer">Observer</option>
        </select>
        <label className="text-xs text-slate-600 flex items-center gap-1 whitespace-nowrap">
          <input
            type="checkbox"
            className="rounded text-indigo-600"
            checked={!!r.required}
            onChange={(e) => onRequiredChange(e.target.checked)}
          />
          Required
        </label>
        <button onClick={onRemove} className="text-xs text-red-500 hover:text-red-700">
          Remove
        </button>
      </div>
      {loaded && hasHistory && (
        <div className="mt-2 pt-2 border-t border-slate-200 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-600">
          <span><strong className="text-slate-900">{stats.invitations}</strong> invitations</span>
          {stats.approval_rate != null && <span><strong className="text-slate-900">{stats.approval_rate}%</strong> approval rate</span>}
          {stats.avg_response_days != null && <span><strong className="text-slate-900">{stats.avg_response_days}d</strong> avg response</span>}
          {stats.counts.abstentions > 0 && <span>{stats.counts.abstentions} abstained</span>}
          {stats.counts.recusals > 0 && <span>{stats.counts.recusals} recused</span>}
        </div>
      )}
      {loaded && !hasHistory && (
        <div className="mt-2 pt-2 border-t border-slate-200 text-[11px] text-slate-400 italic">
          First time on a SignoffHub package — no history yet.
        </div>
      )}
    </div>
  );
}

// =============== Step 3 — Settings ==========================================
function Step3Settings({
  name, setName, description, setDescription, message, setMessage,
  deadline, setDeadline, quorumRule, setQuorumRule, quorumThreshold, setQuorumThreshold,
}) {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-1">Package settings</h3>
      <p className="text-xs text-slate-500 mb-4">Name the package, set the deadline, and decide how quorum is reached.</p>

      <div className="grid gap-4">
        <Field label="Package name *">
          <input value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Q2 2026 Compliance Policy Refresh"
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </Field>
        <Field label="Description">
          <input value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="One-line description shown to reviewers"
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </Field>
        <Field label="Message to reviewers">
          <textarea value={message} onChange={(e) => setMessage(e.target.value)}
            rows={3} placeholder="Context, what to look out for, when you need it back…"
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y" />
        </Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Deadline">
            <input type="datetime-local" value={deadline} onChange={(e) => setDeadline(e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </Field>
          <Field label="Quorum rule">
            <select value={quorumRule} onChange={(e) => setQuorumRule(e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white">
              <option value="all_required">All required reviewers must approve</option>
              <option value="majority_required">Majority of required reviewers</option>
              <option value="custom">Custom threshold</option>
            </select>
            {quorumRule === 'custom' && (
              <input type="number" min={1} value={quorumThreshold}
                onChange={(e) => setQuorumThreshold(e.target.value)}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 mt-2"
                placeholder="Number of approvals needed" />
            )}
          </Field>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

// =============== Step 4 — Review ============================================
const ROLE_LABEL = {
  committee: 'Compliance Committee',
  board:     'Board',
  observer:  'Observer (non-voting)',
};

function Step4Review({ name, description, message, deadline, quorumRule, quorumThreshold, selectedItems, recipients }) {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-1">Review &amp; send</h3>
      <p className="text-xs text-slate-500 mb-4">
        Each reviewer will get a personal, time-bound magic-link. Token hashes never leave the database. Every view and decision is captured to the immutable audit trail.
      </p>

      <div className="grid gap-3 text-sm">
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <div className="text-xs uppercase font-bold tracking-wider text-slate-500 mb-1">Package</div>
          <div className="font-semibold text-slate-800">{name || '(unnamed)'}</div>
          <div className="text-slate-600 text-xs mt-1">{description || '—'}</div>
          {message && (
            <div className="text-slate-700 text-xs mt-2 whitespace-pre-wrap italic">"{message}"</div>
          )}
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <div className="text-xs uppercase font-bold tracking-wider text-slate-500 mb-1">Materials ({selectedItems.length})</div>
          <ul className="text-sm text-slate-700 list-disc ml-5">
            {selectedItems.map((it) => (
              <li key={`${it.artifact_type}:${it.artifact_id}`}>
                {it.title}
                {it.subtitle && <span className="text-slate-400"> — {it.subtitle}</span>}
                {it.version_label && <span className="text-slate-400"> ({it.version_label})</span>}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <div className="text-xs uppercase font-bold tracking-wider text-slate-500 mb-1">Reviewers ({recipients.length})</div>
          <ul className="text-sm text-slate-700">
            {recipients.map((r) => (
              <li key={r.email} className="flex justify-between py-0.5">
                <span>{r.display_name || r.email} <span className="text-slate-400">({ROLE_LABEL[r.role]})</span></span>
                <span className="text-xs text-slate-500">{r.required ? 'Required' : 'Optional'}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 grid grid-cols-2 gap-3 text-xs">
          <div>
            <div className="uppercase font-bold tracking-wider text-slate-500">Deadline</div>
            <div className="text-slate-700 mt-0.5">{deadline ? new Date(deadline).toLocaleString() : '—'}</div>
          </div>
          <div>
            <div className="uppercase font-bold tracking-wider text-slate-500">Quorum</div>
            <div className="text-slate-700 mt-0.5">
              {quorumRule === 'all_required' && 'All required reviewers must approve'}
              {quorumRule === 'majority_required' && 'Majority of required'}
              {quorumRule === 'custom' && `${quorumThreshold} approvals needed`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
